import { isEqual } from 'lodash/fp';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import type { DefaultMatchRequirementMapping } from 'dashboard/models/verification-check';
import {
  VerificationTemplate,
  VerificationTemplateBuilders,
  VerificationTemplateFilterOption,
  VerificationTemplateStatus,
} from 'dashboard/models/verification-template';
import {
  VerificationTemplatesActions,
  VerificationTemplatesActionType,
  VerificationTemplatesFilterParams,
} from 'dashboard/redux/actions/verification-templates';

export const initialVerificationTemplatesFilterState: VerificationTemplatesFilterParams = {
  createdAtStart: undefined,
  createdAtEnd: undefined,
  status: [VerificationTemplateStatus.Active],
  type: [],
};

export interface VerificationTemplatesState extends TableState<VerificationTemplatesFilterParams> {
  verificationTemplates: VerificationTemplate[];
  verificationTemplateFilters: VerificationTemplateFilterOption[];
  verificationTemplateBuilders: VerificationTemplateBuilders;
  defaultMatchRequirements: DefaultMatchRequirementMapping | Record<string, never>;
}

export const initialVerificationTemplatesState: VerificationTemplatesState = {
  verificationTemplates: [],
  verificationTemplateFilters: [],
  verificationTemplateBuilders: {
    'supported-builders': [],
  },
  filters: initialVerificationTemplatesFilterState,
  sortParams: {
    sort: 'updated_at',
    shouldSortDescending: true,
  },
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  defaultMatchRequirements: {},
};

export default (
  state: VerificationTemplatesState = initialVerificationTemplatesState,
  action: VerificationTemplatesActions,
) => {
  switch (action.type) {
    case VerificationTemplatesActionType.SetVerificationTemplates: {
      return {
        ...state,
        verificationTemplates: action.payload.verificationTemplates,
        paginationParams: action.payload.paginationParams,
      };
    }

    case VerificationTemplatesActionType.SetCurrentPage: {
      return {
        ...state,
        currentPage: action.payload.currentPage,
      };
    }

    case VerificationTemplatesActionType.SetPageSize: {
      return {
        ...state,
        pageSize: action.payload.pageSize,
      };
    }

    case VerificationTemplatesActionType.SetFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
        };
      }
    }

    case VerificationTemplatesActionType.SetSortParams: {
      return {
        ...state,
        sortParams: {
          ...state.sortParams,
          ...action.payload.sortParams,
        },
      };
    }

    case VerificationTemplatesActionType.SetDefaultMatchRequirements: {
      return {
        ...state,
        defaultMatchRequirements: action.payload.defaultMatchRequirements,
      };
    }

    case VerificationTemplatesActionType.SetVerificationTemplateBuilders: {
      return {
        ...state,
        verificationTemplateBuilders: action.payload.verificationTemplateBuilders,
      };
    }

    default:
      return state;
  }
};
